import { computed, observable } from 'mobx';
import { flatten, uniq } from 'lodash';

import { Collection } from 'core/model/Collection';

import DeviceModel from './DeviceModel';

const SEARCHABLE_FIELDS = [
  'agentName',
  'appliedLabels',
  'device_manufacturer',
  'device_name',
  'deviceType',
  'flowSource',
  'flowType',
  'id',
  'ip_address',
  'model',
  'os_name',
  'os_version',
  'plan_name',
  'sending_ips',
  'serial_number',
  'site_market_name',
  'site_name',
  'vendor'
];

class DeviceCollection extends Collection<DeviceModel> {
  @observable.ref
  memoryUtilQueryData: {
    device_id: string;
    last_memory_util: number;
    sparklineData: unknown;
  }[] = [];

  @observable.ref
  cpuUtilQueryData: {
    device_id: string;
    'last_total/instant': number;
    sparklineData: unknown;
  }[] = [];

  @observable.ref
  temperatureQueryData: {
    device_id: string;
    avg_instant: number;
    max_instant: number;
    last_instant: number;
    sparklineData: unknown;
  }[] = [];

  @observable.ref
  trafficQueryData: { i_device_name: string; f_sum_both_bytes__k_last: number; sparklineData: unknown }[] = [];

  get defaultSortState(): { field: 'device_name' | 'interfaceCount' | 'percentMatched'; direction: 'desc' | 'asc' } {
    return {
      field: 'device_name',
      direction: 'asc'
    };
  }

  get secondarySort() {
    return this.defaultSortState;
  }

  get url(): string {
    return '/api/ui/devices' as const;
  }

  get model() {
    return DeviceModel;
  }

  useAsyncAdd = true;

  getFilterValues(model: DeviceModel) {
    return SEARCHABLE_FIELDS.map((name) => model[name as keyof DeviceModel] ?? model.get(name)).filter(
      (value) => value != null && value !== ''
    );
  }

  @computed
  get percentUnlabeled() {
    return this.isRequestActive('fetching') ? 0 : Math.floor((this.unlabeledDevices.length / this.size) * 100);
  }

  @computed
  get unlabeledDevices() {
    return this.models.filter((device) => !device.hasLabels);
  }

  @computed
  get deviceIdNameHash() {
    const hash: Record<string | number, string> = {};
    this.models.forEach((device) => {
      hash[device.id as string | number] = device.get('device_name') as string;
    });
    return hash;
  }

  @computed
  get deviceNameIdHash() {
    const hash: Record<string, string | number> = {};
    this.unfiltered.forEach((device) => {
      hash[device.get('device_name') as string] = device.id as string | number;
    });
    return hash;
  }

  // returns a unique array of label names for the selected models
  @computed
  get selectedLabelNames() {
    if (!this.selectedSize) {
      return [];
    }

    const labels = flatten(this.selectedArray.map((m) => (m.get('labels') as { name: string }[]).map((l) => l.name)));
    return uniq(labels);
  }

  getLinkedDeviceModel(model: DeviceModel) {
    if (model.get('device_bgp_type') === 'other_device') {
      return this.models.find((item) => item.get('id') === model.get('use_bgp_device_id')?.toString());
    }

    return null;
  }

  setMemoryUtilQueryData = () => {
    this.memoryUtilQueryData.forEach((result) => {
      const device = this.get(result.device_id);
      if (device && device.get('memory_util') !== result.last_memory_util) {
        device.set('memory_util', result.last_memory_util);
      }
      if (device && device.get('memory_util_timeseries') !== result.sparklineData) {
        device.set('memory_util_timeseries', result.sparklineData);
      }
    });
  };

  setCpuUtilQueryData = () => {
    this.cpuUtilQueryData.forEach((result) => {
      const device = this.get(result.device_id);
      if (device && device.get('cpu_util') !== result['last_total/instant']) {
        device.set('cpu_util', result['last_total/instant']);
      }
      if (device && device.get('cpu_util_timeseries') !== result.sparklineData) {
        device.set('cpu_util_timeseries', result.sparklineData);
      }
    });
  };

  setTemperatureQueryData = () => {
    this.temperatureQueryData.forEach((result) => {
      const device = this.get(result.device_id);
      if (device) {
        device.set('temperature', {
          avg_instant: result.avg_instant,
          max_instant: result.max_instant,
          last_instant: result.last_instant
        });
      }
      if (device && device.get('temperature_timeseries') !== result.sparklineData) {
        device.set('temperature_timeseries', result.sparklineData);
      }
    });
  };

  setTrafficQueryData = () => {
    const { deviceNameIdHash } = this;
    this.trafficQueryData.forEach((result) => {
      const id = deviceNameIdHash[result.i_device_name];
      const device = id ? this.get(id) : null;
      if (device && device.get('traffic') !== result.f_sum_both_bytes__k_last) {
        device.set('traffic', result.f_sum_both_bytes__k_last);
      }
      if (device && device.get('traffic_timeseries') !== result.sparklineData) {
        device.set('traffic_timeseries', result.sparklineData);
      }
    });
  };
}

export default DeviceCollection;
